import { Injectable } from '@angular/core';
import { pluck, take, withLatestFrom } from 'rxjs/operators';
import { Apollo } from 'apollo-angular';
import {
	WorkOrdersQuery,
	WorkSearchOrdersQuery,
} from '@app/pages/cmms/graphql/work-order.queries';
import { EditWorkOrderMutation } from '@app/pages/cmms/graphql/work-order.mutations';
import {
	RequestsQuery,
	RequestsSearchQuery,
} from '@app/pages/cmms/graphql/requests.queries';
import { EditRequest } from '@app/pages/cmms/graphql/request.mutations';
import {
	IncidentsQuery,
	IncidentsSearchQuery,
} from '@app/pages/cmms/graphql/incident.queries';
import { EditIncidentMutation } from '@app/pages/cmms/graphql/incident.mutations';
import { FilesQuery, FilesSearchQuery } from '@app/pages/cmms/graphql/file.queries';
import { EditFileEntityMutation } from '@app/pages/cmms/graphql/files.mutations';
import {
	IAuPaginated,
	IAuPaginationData,
} from '@shared/ui-components/au-paginator/pagination-page.info';
import { startEditFile } from '@app/pages/cmms/components/files/state/files.action';
import { WorkOrdersService } from '@app/pages/cmms/services/work-orders.service';
import { IncidentsService } from '@app/pages/cmms/services/incidents.service';
import { RequestsService } from '@app/pages/cmms/services/requests.service';
import { FilesService } from '@app/pages/cmms/services/files.service';
import { IconSnackBarService } from '@core/services/icon-snack-bar.service';
import { TableType } from '@app/pages/cmms/types';
import { AuRightSidebarService } from '@core/services/au-right-sidebar.service';
import { RightSidebarNavigationService } from '@layout/au-right-sidebar/right-sidebar-navigation.service';
import { Store } from '@ngrx/store';
import { ICmmsState } from '@app/pages/cmms/state/cmms.reducer';
import { EntityType } from '@layout/state/layout.reducer';
import { SelectionModel } from '@angular/cdk/collections';
import { AssetsQuery, SearchAssetsQuery } from '@app/pages/cmms/graphql/assets.queries';
import { object } from '@amcharts/amcharts4/core';
import { AssetsService } from '@app/pages/cmms/services/assets.service';
import { AuErrorAdapter } from '@app/shared';
import {
	AssetMappingEntityTypes,
	ILinkAssetsObjectsVariables,
} from '@app/pages/cmms/interfaces/assets.interfaces';
import { assetWorkspaceIsFromOutside } from '@app/pages/cmms/components/assets/state/assets.selectors';
import { TranslateService } from '@tolgee/ngx';

export const queryByEntityType = {
	work_order: {
		pluckDataKey: 'workOrders',
		loadTableData: WorkOrdersQuery,
		editEntity: EditWorkOrderMutation,
		search: WorkSearchOrdersQuery,
	},
	request: {
		pluckDataKey: 'requests',
		loadTableData: RequestsQuery,
		editEntity: EditRequest,
		search: RequestsSearchQuery,
	},
	incident: {
		pluckDataKey: 'incidents',
		loadTableData: IncidentsQuery,
		editEntity: EditIncidentMutation,
		search: IncidentsSearchQuery,
	},
	files: {
		pluckDataKey: 'files',
		loadTableData: FilesQuery,
		editEntity: EditFileEntityMutation,
		search: FilesSearchQuery,
	},
	assets: {
		pluckDataKey: 'assets',
		loadTableData: AssetsQuery,
		search: SearchAssetsQuery,
	},
};

export const tableTitleByEntityType = {
	work_order: 'work_orders',
	request: 'Requests',
	incident: 'Incidents',
	files: 'Files',
	assets: 'Assets',
};

@Injectable()
export class EntityAttacmentTableService {
	queryByTableEntityType = queryByEntityType;
	editEntityType: EntityType;
	currentEntity: any;
	currentTableType: TableType;
	private selections: SelectionModel<any>;

	setSelections(selections: SelectionModel<any>) {
		this.selections = selections;
	}

	constructor(
		private apollo: Apollo,
		private WOService: WorkOrdersService,
		private incidentService: IncidentsService,
		private requestService: RequestsService,
		private filesService: FilesService,
		private assetsService: AssetsService,
		private notify: IconSnackBarService,
		private sidebarService: AuRightSidebarService,
		private sidebarNavigationService: RightSidebarNavigationService,
		private store$: Store<ICmmsState>,
		private translateService: TranslateService
	) {}

	searchEntity(search: string, pagination: IAuPaginationData) {
		return this.apollo
			.query({
				query: this.queryByTableEntityType[this.currentTableType].search,
				fetchPolicy: 'network-only',
				variables: { search, ...pagination },
			})
			.pipe(
				take(1),
				pluck('data', this.queryByTableEntityType[this.currentTableType].pluckDataKey)
			);
	}

	loadTableData(pagination: IAuPaginationData = { first: 10 }) {
		return this.apollo
			.query<IAuPaginated<any>>({
				query: this.queryByTableEntityType[this.currentTableType].loadTableData,
				fetchPolicy: 'network-only',
				variables: pagination,
			})
			.pipe(
				pluck('data', this.queryByTableEntityType[this.currentTableType].pluckDataKey)
			);
	}

	attachDataToEntity() {
		const methods = {
			WO: () => this.attachDataToWorkorder(),
			INCIDENTS: () => this.attachDataToIncident(),
			REQUEST: () => this.attachDataToRequest(),
			FILE: () => this.attachDataToFile(),
			ASSET: () => this.attachDataForAsset(),
		};

		methods[this.editEntityType]();
		this.sidebarService.setActionButtonStatus(true);
	}

	private attachDataToWorkorder() {
		this.WOService.updateWorkOrder(
			this.getPayloadForUpdateEntity(),
			this.unlinkElements()
		).subscribe(() => {
			this.notify.success(
				`${this.translateService.instant('work_order')} ${this.translateService.instant('has_been_updated_successfully')}`
			);
			this.sidebarService.setActionButtonStatus(false);
			this.sidebarNavigationService.navigateToPreviousPage();
		});
	}

	private attachDataForAsset() {
		const variables: ILinkAssetsObjectsVariables = {
			assets: [this.currentEntity.pk],
		};

		const typeMappings = {
			[TableType.WORK_ORDER]: AssetMappingEntityTypes.WorkOrder,
			[TableType.INCIDENTS]: AssetMappingEntityTypes.Incident,
			[TableType.REQUESTS]: AssetMappingEntityTypes.Request,
			[TableType.FILES]: AssetMappingEntityTypes.FileEntity,
		};

		// TODO: https://linear.app/autility/issue/CMMS-495/[backlog]-improve-consistency-of-naming-of-variables-in-attachment
		const variableMappings = {
			[TableType.WORK_ORDER]: 'workOrders',
			[TableType.INCIDENTS]: 'incidents',
			[TableType.REQUESTS]: 'requests',
			[TableType.FILES]: TableType.FILES,
		};

		if (typeMappings[this.currentTableType]) {
			variables[variableMappings[this.currentTableType]] = this.selections.selected.map(
				value => value.pk
			);
		}

		this.assetsService
			.linkAssetsObjectsVariables(variables, this.unlinkElements())
			.pipe(withLatestFrom(this.store$.select(assetWorkspaceIsFromOutside)))
			.subscribe({
				next: ([_, isFromOutside]) => {
					const type = typeMappings[this.currentTableType];
					const skipTableReload =
						isFromOutside || this.currentTableType === TableType.FILES;

					this.assetsService.afterMappingAssetSuccess(
						this.currentEntity,
						type,
						this.selections.selected,
						skipTableReload
					);

					this.sidebarService.setActionButtonStatus(false);
					this.sidebarNavigationService.navigateToPreviousPage();
				},
				error: error => {
					this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
				},
			});
	}

	private attachDataToIncident() {
		this.incidentService
			.editIncident(this.getPayloadForUpdateEntity(), this.unlinkElements())
			.subscribe(() => {
				this.notify.success(
					`${this.translateService.instant('incident')} ${this.translateService.instant('has_been_updated_successfully')}`
				);
				this.sidebarService.setActionButtonStatus(false);
				this.sidebarNavigationService.navigateToPreviousPage();
			});
	}

	private attachDataToRequest() {
		this.requestService
			.editRequest(
				{
					...this.getPayloadForUpdateEntity(),
					description: this.currentEntity.description,
					name: this.currentEntity.name,
				},
				this.unlinkElements()
			)
			.subscribe(() => {
				this.notify.success(
					`${this.translateService.instant('request')} ${this.translateService.instant('has_been_updated_successfully')}`
				);
				this.sidebarService.setActionButtonStatus(false);
				this.sidebarNavigationService.navigateToPreviousPage();
			});
	}

	private attachDataToFile() {
		this.store$.dispatch(
			startEditFile({
				file: {
					pk: this.currentEntity.pk,
					name: this.currentEntity.name,
					notes: this.currentEntity.notes,
					...this.getPayloadForUpdateEntity(),
				},
				isAttaching: true,
			})
		);

		this.unlinkElements();
		this.sidebarService.setActionButtonStatus(false);
	}

	getPayloadForUpdateEntity() {
		const payloadData: any = { pk: this.currentEntity.pk };
		if (this.currentTableType === TableType.ASSETS) {
			payloadData.assets = this.getUniqueAssetsPayload();
		} else {
			const keys = this.selections.selected.map(value => value.pk);

			if (this.currentTableType === TableType.WORK_ORDER) {
				payloadData.workOrders = keys;
			} else if (this.currentTableType === TableType.INCIDENTS) {
				payloadData.incidents = keys;
			} else if (this.currentTableType === TableType.REQUESTS) {
				payloadData.requests = keys;
			} else if (this.currentTableType === TableType.FILES) {
				payloadData.files = keys;
			}
		}

		return payloadData;
	}

	getAssetMappingEntityTypeByTableType(tableType: TableType): AssetMappingEntityTypes {
		if (tableType === TableType.WORK_ORDER) {
			return AssetMappingEntityTypes.WorkOrder;
		} else if (tableType === TableType.INCIDENTS) {
			return AssetMappingEntityTypes.Incident;
		} else if (tableType === TableType.REQUESTS) {
			return AssetMappingEntityTypes.Request;
		} else if (tableType === TableType.FILES) {
			return AssetMappingEntityTypes.FileEntity;
		}
	}

	private getUniqueAssetsPayload() {
		const assets = this.selections.selected.map(value => {
			return { name: value.name, id: value.pk };
		});

		const uniqueData = {};
		[...assets].forEach(asset => {
			// Delete duplicated data to avoid error
			if (!uniqueData[asset.id]) {
				uniqueData[asset.id] = asset;
			}
		});

		return object.keys(uniqueData).map(key => uniqueData[key]);
	}

	unlinkElements() {
		if (this.currentTableType === 'assets') return;

		const payload = this.getPayloadForUpdateEntity();
		const unlink = [];

		const entityType = (currentType: string) => {
			const lowerCurrentType = currentType.toLowerCase();

			if (['wo', 'work_order'].includes(lowerCurrentType)) {
				return { name: 'workOrders', data: this.currentEntity?.workOrders };
			}
			if (['incidents', 'incident'].includes(lowerCurrentType)) {
				return {
					name: 'incidents',
					data:
						this.currentEntity?.incidentWorkOrders?.edges ||
						this.currentEntity?.incidents,
				};
			}
			if (['request'].includes(lowerCurrentType)) {
				return {
					name: 'requests',
					data:
						this.currentEntity?.requestWorkOrders?.edges ||
						this.currentEntity?.requestIncident?.edges ||
						this.currentEntity.requests,
				};
			}
			if (['asset'].includes(lowerCurrentType)) {
				return {
					name: 'assets',
					data: this.currentEntity?.assetAttachedFiles,
				};
			}
			return;
		};

		if (payload.files) {
			const files = this.currentEntity.files || this.currentEntity.assetAttachedFiles;
			files.forEach(
				(item: { pk: string; importConfirmed: boolean; importId: string }) => {
					if (item.importConfirmed || !item.importId) {
						if (!payload.files.includes(item.pk) && this.editEntityType === 'ASSET') {
							unlink.push({
								pk: payload.pk,
								files: item.pk,
							});
						} else {
							this.filesService
								.unlinkAttachedElements({
									pk: item.pk,
									[entityType(this.editEntityType).name]: payload.pk,
								})
								.subscribe({
									error: error => {
										this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
									},
								});
						}
					}
				}
			);
		} else {
			entityType(this.currentTableType).data.map(
				(item: { node: { pk: string }; pk: string }) =>
					!payload[entityType(this.currentTableType).name].includes(
						item.node ? item.node.pk : item.pk
					) &&
					unlink.push({
						pk: payload.pk,
						[entityType(this.currentTableType).name]: item.node ? item.node.pk : item.pk,
					})
			);
		}

		return unlink;
	}
}
